import Papa from 'papaparse';
import { JSONParser } from '@streamparser/json';

/**
 * Parses and validates a CSV or JSON file for importing batch results
 * @param {File} file The file to import (CSV or JSON)
 * @param {Function} onProgress Progress callback (0-100)
 * @returns {Promise<Object>} The parsed and validated data
 */
export async function importBatchData(file, onProgress = () => {}) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'json'].includes(fileExtension)) {
      reject(new Error('Please upload a CSV or JSON file'));
      return;
    }

    if (fileExtension === 'json') {
      handleJsonImport(file, resolve, reject, onProgress);
    } else {
      handleCsvImport(file, resolve, reject, onProgress);
    }
  });
}

/**
 * Ensures a value is a string
 * @param {any} value The value to convert
 * @param {string} defaultValue Optional default value if conversion fails
 * @returns {string} The string value
 */
function ensureString(value, defaultValue = '') {
  if (value === null || value === undefined) return defaultValue;
  return String(value);
}

/**
 * Handles JSON file import using streaming parser
 * @param {File} file The JSON file to import
 * @param {Function} resolve Promise resolve function
 * @param {Function} reject Promise reject function
 * @param {Function} onProgress Progress callback (0-100)
 */
function handleJsonImport(file, resolve, reject, onProgress) {
  const storeMap = new Map();
  let isFirstChunk = true;
  let totalBytes = file.size;
  let processedBytes = 0;

  // Create a streaming parser instance
  const p = new JSONParser({
    paths: ['$.*'], // Match all top-level array elements
    keepStack: false,
    stringBufferSize: undefined,
    numberBufferSize: undefined,
    separator: '.',
    streamValues: false,
    strict: true
  });

  // Handle complete store objects
  p.onValue = ({ value }) => {
    try {
      if (isFirstChunk) {
        isFirstChunk = false;
        return; // Skip the first empty value
      }
      
      // Ensure text fields are strings
      value.storeUrl = ensureString(value.storeUrl);
      if (Array.isArray(value.freeProducts)) {
        value.freeProducts = value.freeProducts.map(product => ({
          ...product,
          id: ensureString(product.id),
          title: ensureString(product.title),
          handle: ensureString(product.handle),
          productUrl: ensureString(product.productUrl),
          featuredImage: ensureString(product.featuredImage),
          freeVariants: Array.isArray(product.freeVariants) ? product.freeVariants.map(variant => ({
            ...variant,
            id: ensureString(variant.id),
            title: ensureString(variant.title),
            price: typeof variant.price === 'number' ? variant.price : parseFloat(variant.price) || 0,
            available: Boolean(variant.available)
          })) : []
        }));
      }
      
      validateStoreStructure(value);
      storeMap.set(value.storeUrl, value);
    } catch (error) {
      p.destroy();
      reject(new Error('Invalid JSON format: ' + error.message));
    }
  };

  // Handle end of parsing
  p.onEnd = () => {
    const results = Array.from(storeMap.values());
    resolve({
      success: true,
      totalLinksProcessed: results.length,
      validStoresFound: results.length,
      storesWithFreeProducts: results.length,
      results
    });
  };

  // Handle parsing errors
  p.onError = (error) => {
    reject(new Error('Invalid JSON format: ' + error.message));
  };

  // Stream the file content to the parser
  const reader = file.stream().getReader();
  (async () => {
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        processedBytes += value.length;
        onProgress((processedBytes / totalBytes) * 100);
        p.write(value);
      }
      p.end();
    } catch (error) {
      reject(new Error('Error reading file: ' + error.message));
    }
  })();
}

/**
 * Handles CSV file import using PapaParse
 * @param {File} file The CSV file to import
 * @param {Function} resolve Promise resolve function
 * @param {Function} reject Promise reject function
 * @param {Function} onProgress Progress callback (0-100)
 */
function handleCsvImport(file, resolve, reject, onProgress) {
  const storeMap = new Map();
  const expectedColumns = [
    'Store URL',
    'Product ID',
    'Product Title',
    'Product Handle',
    'Product URL',
    'Featured Image',
    'Variant ID',
    'Variant Title',
    'Price',
    'Available'
  ];

  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true,
    chunk: ({ data, errors, meta }) => {
      try {
        // Validate headers on first chunk
        if (meta.cursor === 0) {
          const missingColumns = expectedColumns.filter(col => !meta.fields.includes(col));
          if (missingColumns.length > 0) {
            throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
          }
        }

        // Process each row in the chunk
        for (const row of data) {
          const storeUrl = ensureString(row['Store URL']);
          if (!storeUrl) continue;

          if (!storeMap.has(storeUrl)) {
            storeMap.set(storeUrl, {
              storeUrl,
              freeProducts: new Map()
            });
          }

          const store = storeMap.get(storeUrl);
          const productId = ensureString(row['Product ID']);

          if (!store.freeProducts.has(productId)) {
            store.freeProducts.set(productId, {
              id: productId,
              title: ensureString(row['Product Title']),
              handle: ensureString(row['Product Handle']),
              productUrl: ensureString(row['Product URL']),
              featuredImage: ensureString(row['Featured Image']),
              freeVariants: []
            });
          }

          const product = store.freeProducts.get(productId);
          product.freeVariants.push({
            id: ensureString(row['Variant ID']),
            title: ensureString(row['Variant Title']),
            price: typeof row['Price'] === 'number' ? row['Price'] : parseFloat(row['Price']) || 0,
            available: typeof row['Available'] === 'boolean' ? row['Available'] : String(row['Available']).toLowerCase() === 'true'
          });
        }

        // Update progress based on bytes processed
        onProgress((meta.cursor / file.size) * 100);
      } catch (error) {
        reject(new Error('Invalid CSV format: ' + error.message));
      }
    },
    complete: () => {
      const results = Array.from(storeMap.values()).map(store => ({
        ...store,
        freeProducts: Array.from(store.freeProducts.values())
      }));

      resolve({
        success: true,
        totalLinksProcessed: results.length,
        validStoresFound: results.length,
        storesWithFreeProducts: results.length,
        results
      });
    },
    error: (error) => {
      reject(new Error('Invalid CSV format: ' + error.message));
    },
    worker: true // Use Web Worker for non-blocking parsing
  });
}

/**
 * Validates a single store object structure
 * @param {Object} store The store object to validate
 * @throws {Error} If the store structure is invalid
 */
function validateStoreStructure(store) {
  if (!store.storeUrl || typeof store.storeUrl !== 'string') {
    throw new Error('Invalid store format: Missing or invalid storeUrl');
  }

  if (!Array.isArray(store.freeProducts)) {
    throw new Error('Invalid store format: freeProducts must be an array');
  }

  for (const product of store.freeProducts) {
    const requiredFields = ['id', 'title', 'handle', 'productUrl'];
    const missingFields = requiredFields.filter(field => !product[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Invalid product format: Missing required field(s): ${missingFields.join(', ')}`);
    }

    if (!Array.isArray(product.freeVariants)) {
      throw new Error('Invalid product format: freeVariants must be an array');
    }

    for (const variant of product.freeVariants) {
      const variantFields = ['id', 'title', 'price', 'available'];
      const missingVariantFields = variantFields.filter(field => variant[field] === undefined);
      
      if (missingVariantFields.length > 0) {
        throw new Error(`Invalid variant format: Missing required field(s): ${missingVariantFields.join(', ')}`);
      }
    }
  }
} 